import { useState, useEffect } from "react"
import { useAuthContext } from "@context/AuthProvider"
import {
	useLegalAcceptanceMutations,
	useLegalAcceptanceQuery
} from "./useLegalAcceptanceMutations"
import { useAEMComponentData } from "@AEM/hooks/useAEMComponentData"
import {
	AEMComponentMapper,
	AEMComponentType,
	AEMPageType
} from "@AEM/models/AEMPage.model"
import { LegalAcceptanceRequest } from "@pages/profile-managment/services/agentService"
import { VITE_USE_LEGAL_ACCEPTANCE } from "@utils/constants.util"
import secureLocalStorage from "react-secure-storage"
import { AvailableLanguages, StorageKeys } from "@utils/user-config"

export const useLegalAcceptance = () => {
	const [showTCModal, setShowTCModal] = useState(false)
	const [needsTravelAgentGuidelines, setNeedsTravelAgentGuidelines] =
		useState(false)
	const [needsTermsOfUse, setNeedsTermsOfUse] = useState(false)
	const { isAuthenticated, isLoading } = useAuthContext()

	const { submitLegalAcceptanceMutation } = useLegalAcceptanceMutations()

	const { componentData: travelAgentGuidelinesData } = useAEMComponentData(
		AEMPageType?.PAGES_TRAVEL_AGENT_GUIDELINES,
		AEMComponentMapper.SEARCHABLE_LONG_FORM_CONTENT
	)
	const { componentData: termsOfUseData } = useAEMComponentData(
		AEMPageType?.PAGES_TERMS_OF_USE,
		AEMComponentMapper.SEARCHABLE_LONG_FORM_CONTENT
	)

	const language = ((secureLocalStorage.getItem(
		StorageKeys.USER_PREFERRED_LANGUAGE
	) as string) || AvailableLanguages.EN_US) as string

	// Only enable the query when we have all required data and user is authenticated
	const shouldEnableQuery =
		isAuthenticated &&
		!isLoading &&
		!!travelAgentGuidelinesData &&
		!!termsOfUseData &&
		VITE_USE_LEGAL_ACCEPTANCE

	const {
		data: userLegalAcceptance,
		isLoading: isCheckingTC,
		error: legalAcceptanceError
	} = useLegalAcceptanceQuery(shouldEnableQuery)

	const isTermsOfUseRoute = location.pathname.includes("/terms-of-use")
	const isTravelAgentGuidelinesRoute = location.pathname.includes(
		"/travel-agent-guidelines"
	)
	useEffect(() => {
		// TODO: TravelAgentGuidelinesData is not required on this version
		if (!userLegalAcceptance || !travelAgentGuidelinesData || !termsOfUseData) {
			return
		}
		const checkTravelAgentGuidelines = false /* !checkVersion(
			travelAgentGuidelinesData,
			userLegalAcceptance.acceptedTerms?.find(
				(term) =>
					term.termType ===
					(AEMPageType?.PAGES_TRAVEL_AGENT_GUIDELINES as string)
			)
		) */
		const checkTermsOfUse = !checkVersion(
			termsOfUseData,
			userLegalAcceptance.acceptedTerms?.find(
				(term: {
					policyType: string
					policyVersion: string
					policyLanguage: string
				}) => term.policyType === AEMPageType?.PAGES_TERMS_OF_USE
			)
		)

		setNeedsTravelAgentGuidelines(false)
		setNeedsTermsOfUse(checkTermsOfUse)
		if (isTermsOfUseRoute || isTravelAgentGuidelinesRoute) {
			setShowTCModal(false)
		} else {
			setShowTCModal(checkTravelAgentGuidelines || checkTermsOfUse)
		}
	}, [
		userLegalAcceptance,
		travelAgentGuidelinesData,
		termsOfUseData,
		isTermsOfUseRoute,
		isTravelAgentGuidelinesRoute
	])

	const checkVersion = (
		aemTerm: AEMComponentType,
		userTerm:
			| { policyType: string; policyVersion: string; policyLanguage: string }
			| undefined
	) => {
		if (!userTerm || !aemTerm?.modifiedDate) {
			return false
		}
		return aemTerm?.modifiedDate === userTerm?.policyVersion
	}

	const handleAcceptTerms = async () => {
		try {
			const acceptanceData: LegalAcceptanceRequest = {
				acceptedTerms: [
					...(needsTravelAgentGuidelines &&
					travelAgentGuidelinesData?.modifiedDate
						? [
								{
									policyType: AEMPageType?.PAGES_TRAVEL_AGENT_GUIDELINES,
									policyVersion:
										travelAgentGuidelinesData?.modifiedDate as string,
									policyLanguage: language
								}
							]
						: []),
					...(needsTermsOfUse && termsOfUseData?.modifiedDate
						? [
								{
									policyType: AEMPageType?.PAGES_TERMS_OF_USE,
									policyVersion: termsOfUseData?.modifiedDate as string,
									policyLanguage: language
								}
							]
						: [])
				]
			}

			await submitLegalAcceptanceMutation.mutateAsync(acceptanceData)
		} catch (error) {
			console.error("Error accepting terms:", error)
		}
	}

	const closeTCModal = () => {
		setShowTCModal(false)
	}

	return {
		showTCModal,
		closeTCModal,
		isCheckingTC,
		handleAcceptTerms,
		needsTravelAgentGuidelines,
		needsTermsOfUse,
		isAcceptingTerms: submitLegalAcceptanceMutation.isPending || isCheckingTC,
		acceptanceError: submitLegalAcceptanceMutation.error,
		legalAcceptanceError
	}
}
